import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';

// Components
import Icon from '@/components/base/Icon';
import Instagram from '@/components/icons/Instagram';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';

// Lib
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TWITTER,
    TIKTOK_BUSINESS,
    TIKTOK,
    YOUTUBE
} from '@/lib/socialProfile';

// Utils
import { getAnalyticsGroupUsedNetworkTypes } from '@/utils/analytics/getAnalyticsGroupUsedNetworkTypes';

// Types
import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

type AggregatedNetworksListProps = {
    group: SpGroupType;
    aggregatedNetworkTypes: SocialProfileNetworkType[];
    forceLightMode?: boolean;
    className?: string;
};

const NetworkIcon = ({
    networkType,
    forceLightMode
}: {
    networkType: SocialProfileNetworkType;
    forceLightMode: boolean;
}) => {
    switch (networkType) {
        case INSTAGRAM:
            return <Instagram key={networkType} size={12} />;
        case PINTEREST:
            return <PinterestIcon key={networkType} size={12} />;
        case YOUTUBE:
            return <YoutubeIcon key={networkType} size={12} />;
        default:
            return (
                <Icon
                    data-testid={`logo-${networkType}`}
                    key={networkType}
                    icon={networkType ?? ''}
                    className={clsx('text-xs', {
                        'text-facebook': networkType === FACEBOOK,
                        'text-linkedin': networkType === LINKEDIN,
                        'text-twitter': networkType === TWITTER,
                        'text-tiktok': networkType === TIKTOK || networkType === TIKTOK_BUSINESS,
                        'dark:text-white': !forceLightMode
                    })}
                />
            );
    }
};

const AggregatedNetworksList = ({
    group,
    aggregatedNetworkTypes,
    forceLightMode = false,
    className
}: AggregatedNetworksListProps) => {
    const { t } = useTranslation('analytics');

    if (!group || !aggregatedNetworkTypes) {
        return null;
    }

    const usedNetworkTypes = getAnalyticsGroupUsedNetworkTypes(group, aggregatedNetworkTypes, true);

    if (!usedNetworkTypes?.length) {
        return null;
    }

    return (
        <div className={clsx('font-GMedium text-xxs text-icoGray-500', className)}>
            <p>{t('data_aggregated_from')}</p>
            <div className="flex space-x-2">
                {usedNetworkTypes.map((networkType: SocialProfileNetworkType) => (
                    <NetworkIcon
                        key={networkType}
                        networkType={networkType}
                        forceLightMode={forceLightMode}
                    />
                ))}
            </div>
        </div>
    );
};

export default AggregatedNetworksList;
